/**
 * Utility functions for MonthRangePicker component and month-based date operations
 */

/**
 * Converts a YYYY-MM string to a Date object
 * @param monthStr - Month string in YYYY-MM format
 * @returns Date object or null if invalid
 */
export const stringToDate = (monthStr: string): Date | null => {
    if (!monthStr || !/^\d{4}-\d{2}$/.test(monthStr)) return null;
    const [year, month] = monthStr.split('-').map(Number);
    return new Date(year, month - 1, 1);
};

/**
 * Converts a Date object to YYYY-MM string
 * @param date - Date object
 * @returns Month string in YYYY-MM format or empty string if null
 */
export const dateToString = (date: Date | null): string => {
    if (!date) return '';
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    return `${year}-${month}`;
};

/**
 * Validates that start month is before or equal to end month
 * @param start - Start month in YYYY-MM format
 * @param end - End month in YYYY-MM format
 * @returns true if valid range
 */
export const isValidRange = (start: string, end: string): boolean => {
    if (!start || !end) return false;
    return start <= end; // String comparison works for YYYY-MM format
};

/**
 * Returns a Date object for N months ago from current date
 * @param n - Number of months to go back (default: 1)
 * @returns Date object set to the first day of the month N months ago
 */
export const getMonthsAgoDate = (n = 1): Date => {
    const date = new Date();
    date.setDate(1); // Set to 1st BEFORE changing month to avoid overflow
    date.setMonth(date.getMonth() - n);
    return date;
};

/**
 * Converts a Date object to YYYYMM string format (no separator)
 * @param date - Date object
 * @returns Month string in YYYYMM format or empty string if null
 */
export const dateToYYYYMM = (date: Date | null): string => {
    return date ? dateToString(date).replace('-', '') : '';
};
